import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import objectivesService from 'services/objectivesService';
import { useAuth } from 'contexts/AuthContext';

const STORAGE_KEY = 'gm_objectives_v1';

const ObjectivesContext = createContext(null);

export const useObjectives = () => {
  const ctx = useContext(ObjectivesContext);
  if (!ctx) throw new Error('useObjectives debe usarse dentro de ObjectivesProvider');
  return ctx;
};

export const ObjectivesProvider = ({ children }) => {
  const [objectives, setObjectives] = useState([]);
  const { filterByAssignedClients } = useAuth();

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setObjectives(JSON.parse(raw));
    } catch (_) {
      // noop
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(objectives));
    } catch (_) {
      // noop
    }
  }, [objectives]);

  const createObjective = async (payload) => {
    const result = await objectivesService.create(payload);
    if (result?.success) {
      const saved = result.data;
      setObjectives(prev => [saved, ...prev]);
      return saved;
    } else {
      // fallback local si el backend falla
      const now = new Date().toISOString();
      const newItem = { id: uuidv4(), createdAt: now, updatedAt: now, status: 'active', ...payload };
      setObjectives(prev => [newItem, ...prev]);
      return newItem;
    }
  };

  // Cargar desde backend al iniciar
  React.useEffect(() => {
    (async () => {
      try {
        const res = await objectivesService.list();
        if (res?.success && Array.isArray(res.data)) setObjectives(res.data);
      } catch (_) {}
    })();
  }, []);

  const updateObjective = async (id, patch) => {
    try {
      const res = await objectivesService.update(id, patch);
      const saved = res?.success ? res.data : { id, ...patch };
      setObjectives(prev => prev.map(o => o.id === id ? { ...o, ...saved, updatedAt: new Date().toISOString() } : o));
      return saved;
    } catch (_) {
      setObjectives(prev => prev.map(o => o.id === id ? { ...o, ...patch, updatedAt: new Date().toISOString() } : o));
      return { id, ...patch };
    }
  };

  const removeObjective = async (id) => {
    try {
      await objectivesService.remove(id);
    } catch (_) {}
    setObjectives(prev => prev.filter(o => o.id !== id));
  };

  // Filtrar objetivos por clientes asignados al usuario
  const filteredObjectives = useMemo(() => {
    return filterByAssignedClients(objectives, 'clientId');
  }, [objectives, filterByAssignedClients]);

  const value = useMemo(() => ({ 
    objectives: filteredObjectives, 
    allObjectives: objectives, // Para uso interno si se necesita
    createObjective, 
    updateObjective, 
    removeObjective 
  }), [filteredObjectives, objectives]);

  return (
    <ObjectivesContext.Provider value={value}>
      {children}
    </ObjectivesContext.Provider>
  );
};

export default ObjectivesContext;


